import { Box } from 'grommet';
import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components/macro';
import { Drop, themeStyles, base } from '@sdxcorp/sdx-dds-gui-lib';

import { colorValue, defaultTransition } from '../../../../lib/theme';
import { useAuth } from '../../../auth/useAuth';
import { AdminAuthContext } from '../../../../admin/auth/authContext';
import { useTranslation } from 'react-i18next';

const { darkTheme } = themeStyles;

const {
  atoms: { Icons, LabelValue, IconButton },
} = base;

const StyledUserIcon = styled(Icons.UserNeutral)`
  color: ${({ theme }) => colorValue(theme, c => c['grey-80'])};
  width: 24px;
  height: 24px;
`;

const StyledChevronDown = styled(Icons.ChevronDown)`
  color: ${({ theme }) => colorValue(theme, c => c['grey-80'])};
`;

const StyledLabel = styled(LabelValue.Label)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  text-transform: none;
  font-weight: 400;
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  color: ${({ theme }) => colorValue(theme, c => c['grey-90'])};
  user-select: none;

  flex: 1 0 auto;
`;

const StyledValue = styled(LabelValue.Value)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-size: 14px;
  font-weight: 700;
  line-height: 17px;
  color: ${({ theme }) => colorValue(theme, c => c['grey-90'])};
  user-select: none;
`;

interface StyledBoxProps {
  menuOpen: boolean;
  testId: string;
}

const StyledBox = styled(Box).attrs(({ testId }: StyledBoxProps) => ({
  'data-test-id': `button.${testId}`,
}))<StyledBoxProps>`
  transition: ${defaultTransition('background-color')};
  padding: 8px 12px;
  border-radius: 3px;
  cursor: pointer;
  background-color: ${props => (props.menuOpen ? colorValue(props.theme, c => c['grey-20']) : colorValue(props.theme, c => c['white']))};
  &:hover {
    background-color: ${props => (props.menuOpen ? colorValue(props.theme, c => c['grey-20']) : colorValue(props.theme, c => c['grey-10']))};
  }
`;

const StyledDrop = styled(Drop)`
  margin-top: 10px;
  border-radius: 3px;
  background-color: ${props => colorValue(props.theme, c => c['white'])};
`;

StyledDrop.displayName = 'UserInfoStyledDrop';

const StyledOptionBox = styled(Box)`
  padding: 8px 0 8px 20px;
  cursor: pointer;
  &:hover {
    background-color: ${props => colorValue(props.theme, c => c['grey-10'])};
  }
`;

const StyledOptionText = styled(LabelValue.Value)`
  font-size: 15px;
  font-weight: 700;
  color: ${props => colorValue(props.theme, c => c['con-error'])};
`;

const UserInfo = () => {
  const { t } = useTranslation();
  const { user, member } = useAuth(AdminAuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = React.useRef<any>(null);
  const toggleDrop = () => setMenuOpen(!menuOpen);
  const logout = () => (window.location.href = '/logout');

  // wrapping element with display:box needed for IE flexbox issue fix
  return (
    <ThemeProvider theme={darkTheme}>
      <StyledBox flex direction="row" align="center" testId="userInfo" onClick={toggleDrop} menuOpen={menuOpen} ref={ref}>
        <StyledUserIcon />
        <Box margin={{ left: 'small', right: 'small' }}>
          <StyledValue>{user && user.name}</StyledValue>
          <StyledLabel testId="userInfo-name">{member && member.name}</StyledLabel>
        </Box>
        <StyledChevronDown />
        {menuOpen && (
          <StyledDrop
            testId="userInfo-drop"
            target={ref.current}
            onClickOutside={toggleDrop}
            onEsc={toggleDrop}
            align={{ top: 'bottom' }}
            elevation="large"
          >
            <StyledOptionBox direction="row" align="center" onClick={logout}>
              <IconButton.default testId="user-logout" icon={Icons.Logout} size="small" color="red" />
              <StyledOptionText>{t('navigation.user.logout')}</StyledOptionText>
            </StyledOptionBox>
          </StyledDrop>
        )}
      </StyledBox>
    </ThemeProvider>
  );
};

export default React.memo(UserInfo);
